import {
  Badge,
  Button,
  Card,
  Divider,
  DropdownButton,
  IconButton,
  Tooltip,
} from '@common/material'
import type { DropdownButtonOption } from '@common/material/DropdownButton/types'
import { useModal } from '@common/GlobalModal'
import { useMemo } from 'react'
import { RegisterItemInspectionProperty } from '@core/entities/register/RegisterItem/types'
import { RegisterInspection } from '@core/entities/register/RegisterInspection'
import { PageUrls } from '@core/page-urls'
import { localizedStrings } from '@core/strings'
import { Palette } from '@core/theme/color-palette-siteassist'
import { Flex, Text } from '@fundamentals'
import {
  AssignmentTurnedInOutlined,
  MoreHoriz,
  PlayCircleOutline,
  PlaylistAddCheckOutlined,
} from '@mui/icons-material'
import moment from 'moment'
import { useRouter } from 'next/router'
import { NewInspectionModal } from '../../modals/NewInspectionModal'
import { encodeReturnUrl } from 'webapp/utils/returnUrl'
import { RegisterInspectionCardDetailRow } from './RegisterInspectionCardDetailRow'
import { InspectionDetailsClickParams } from './RegisterScheduleInspectionsCard'
import { useDrawer } from '@common/GlobalDrawer'

export interface RegisterInspectionCardProps {
  organisationId: number
  registerId: number
  registerItemId: number
  projectId?: number
  inspectionConfig: RegisterItemInspectionProperty<string>
  onInspectionDetailsClick?: (params: InspectionDetailsClickParams) => void
}

export const RegisterInspectionCard = ({
  organisationId,
  registerId,
  registerItemId,
  projectId,
  inspectionConfig,
  onInspectionDetailsClick,
}: RegisterInspectionCardProps) => {
  const { showModal } = useModal()
  const { hideDrawer } = useDrawer()
  const router = useRouter()

  const inspection = RegisterInspection.new(inspectionConfig.inspection)
  const checklistTemplates = inspection.checklistTemplates ?? []

  const lastInspectedBy = inspection.lastInspectedBy
  const lastInspectionMoment = moment(inspection.lastInspectionTime)
  const hasLastCompletedTime =
    !!inspection.lastInspectionTime && lastInspectionMoment.isValid()
  const lastInspected = lastInspectionMoment.format('DD MMM YYYY HH:mm')

  const hasNextInspection = !!inspection.nextInspectionDue
  const nextInspectionDue = moment(inspection.nextInspectionDue).format(
    'DD MMM YYYY HH:mm',
  )

  const canSubmitInspection = !!projectId && checklistTemplates.length > 0

  const runInspection = () => {
    if (!canSubmitInspection) return
    if (checklistTemplates.length === 1) {
      const returnUrl = encodeReturnUrl({
        pathname: router.pathname,
        query: router.query,
      })
      router.push({
        pathname: PageUrls.newChecklistProject({
          organisationId,
          projectId,
        }),
        query: {
          registerId,
          registerItemId,
          projectId,
          registerTemplateInspectionId: inspection.registerTemplateInspectionId,
          inspectionTemplateId: checklistTemplates[0].id,
          returnUrl,
          isUsedForInspections: true,
        },
      })
      hideDrawer()
      return
    }
    showModal(
      <NewInspectionModal
        organisationId={organisationId}
        projectId={projectId}
        registerId={registerId}
        registerItemId={registerItemId}
        inspections={[inspectionConfig]}
        registerTemplateInspectionId={inspection.registerTemplateInspectionId}
      />,
    )
  }

  const menuOptions = useMemo(() => {
    const options: DropdownButtonOption[] = [
      {
        title: localizedStrings.runInspection,
        'data-test': 'new-inspection-button',
        icon: <PlayCircleOutline />,
        onSelect: runInspection,
      },
    ]
    if (onInspectionDetailsClick) {
      options.push({
        title: localizedStrings.viewCompletedInspections,
        icon: <PlaylistAddCheckOutlined />,
        onSelect: () =>
          onInspectionDetailsClick({
            inspection,
            config: inspectionConfig,
            initialTab: 'inspections',
          }),
      })
      options.push({
        title: localizedStrings.viewTask,
        icon: <AssignmentTurnedInOutlined />,
        onSelect: () =>
          onInspectionDetailsClick({
            inspection,
            config: inspectionConfig,
            initialTab: 'tasks',
          }),
      })
    }
    return options
  }, [onInspectionDetailsClick])

  if (!inspectionConfig.inspection) {
    return null
  }

  return (
    <Card
      variant='outlined'
      onClick={() =>
        onInspectionDetailsClick({ inspection, config: inspectionConfig })
      }
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        borderColor: 'divider',
        px: 2,
        py: 1.5,
        '&:hover': {
          borderColor: Palette.Grey[400],
          '& .show-on-hover': { visibility: 'visible' },
        },
        ...(onInspectionDetailsClick ? { cursor: 'pointer' } : {}),
      }}
    >
      <Flex flexDirection='column' gap={1.5}>
        <Flex justifyContent='space-between' alignItems='flex-start' gap={1}>
          <Text
            variant='body2'
            fontWeight={500}
            color='text.primary'
            sx={{
              minWidth: 0,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {inspectionConfig.name}
          </Text>
          {canSubmitInspection && (
            <Tooltip title={localizedStrings.options} arrow>
              <DropdownButton
                label={localizedStrings.options}
                options={menuOptions}
                renderButton={(onClick) => (
                  <IconButton
                    aria-label={localizedStrings.actions}
                    size='small'
                    className='show-on-hover'
                    onClick={(e) => {
                      e.stopPropagation()
                      onClick(e)
                    }}
                    sx={{
                      color: 'text.secondary',
                      mt: -0.5,
                      mr: -1,
                      flexShrink: 0,
                      visibility: 'hidden',
                    }}
                  >
                    <MoreHoriz />
                  </IconButton>
                )}
              />
            </Tooltip>
          )}
        </Flex>

        <Flex flexDirection='column' gap={1}>
          <RegisterInspectionCardDetailRow
            label={localizedStrings.appliedTo}
            value={localizedStrings.allItems}
          />
          {hasLastCompletedTime && (
            <RegisterInspectionCardDetailRow
              label={localizedStrings.lastCompleted}
              value={lastInspected}
            />
          )}
          {lastInspectedBy && (
            <RegisterInspectionCardDetailRow
              label={localizedStrings.completedBy}
              value={`${lastInspectedBy.firstName} ${lastInspectedBy.lastName}`}
            />
          )}
          {hasNextInspection && (
            <RegisterInspectionCardDetailRow
              label={localizedStrings.nextInspectionDue}
              value={
                <Text
                  variant='body2'
                  color={RegisterInspection.getDueDateColor(
                    inspection.nextInspectionDue,
                  )}
                >
                  {nextInspectionDue}
                </Text>
              }
            />
          )}
          {inspection.openTasks !== undefined && (
            <RegisterInspectionCardDetailRow
              label={localizedStrings.openTasks}
              value={<Badge count={inspection.openTasks} hideZero={false} />}
            />
          )}
        </Flex>

        <Divider sx={{ my: 0 }} />

        <RegisterInspectionCardDetailRow
          label={localizedStrings.completedRuns}
          value={
            <Badge count={inspection.completedRuns ?? 0} hideZero={false} />
          }
        />

        {canSubmitInspection ? (
          <Button
            variant='text'
            color='primary'
            size='medium'
            startIcon={<PlayCircleOutline sx={{ fontSize: 20 }} />}
            onClick={runInspection}
            sx={{
              alignSelf: 'flex-start',
              px: 1,
              ml: -1,
            }}
          >
            {localizedStrings.runInspection}
          </Button>
        ) : null}
      </Flex>
    </Card>
  )
}
